import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import ConversationTopicsMenuV2 from 'components/customer/conversation_history/topics_v2/conversation_topics_menu_v2';
import ConversationTopicsMenuOpenerV2 from 'components/customer/conversation_history/topics_v2/conversation_topics_menu_opener_v2';
import {
  COLLAPSE_SELECTED_OPTIONS_NUM,
  SelectAction,
  TopicsMenuOptionType,
} from 'components/customer/conversation_history/topics_v2/conversation_topics_constants_v2';
import filterTopicOptions from 'components/customer/conversation_history/topics_v2/filter_topic_options';
import { CustomAttributeValue } from 'models/custom_attribute';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';

export default class ConversationTopicsMultiSelectV2 extends PureComponent {
  /* life cycle */

  constructor(props) {
    super(props);

    let selectedValues = this.getInitialSelectedValues();
    let options = this.getOptions(null, this.props.options, selectedValues, this.props.suggestedIds || []);
    this.state = {
      filter: '',
      selectionsExpanded: false,
      selectedValues,
      opened: false,
      options,
    };
    this.suppressNextMouseEvent = true;

    _.bindAll(this, [
      'blur',
      'getInitialFocusedOptionId',
      'focus',
      'handleApply',
      'handleBlur',
      'handleCancel',
      'handleClose',
      'handleCollapseSelections',
      'handleExpandSelections',
      'handleHeaderClick',
      'handleInputChange',
      'handleInputKeyDown',
      'handleMenuFocus',
      'handleMenuOpenerClick',
      'handleMenuOpenerToggleDown',
      'handleOpen',
      'handleOptionClick',
      'handleOptionFocus',
      'handleOutsideClick',
      'handleSubmit',
      'renderMenu',
      'setMenuRef',
      'setOpenerRef',
    ]);
  }

  componentWillUnmount() {
    clearTimeout(this._scheduledFocus);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    let nextPropOptions = nextProps.options;

    let selectedValues = this.state.selectedValues;
    if (
      !_.isEqual(this.props.optionIds, nextProps.optionIds) ||
      !_.isEqual(this.props.selectedCustomAttributes, nextProps.selectedCustomAttributes)
    ) {
      let nextOptionIds = nextProps.optionIds || [];
      let nextAttributeValues = nextProps.selectedCustomAttributes || [];

      selectedValues = { attributeValues: nextAttributeValues, optionIds: nextOptionIds };

      this.setState({
        selectedValues,
      });
    }

    let nextOptions = this.getOptions(
      this.state.options,
      nextPropOptions,
      selectedValues,
      nextProps.suggestedIds || []
    );
    const hasNewOptions = nextOptions.length !== this.state.options.length;
    const hasChangedOptions = _.intersection(nextOptions, this.state.options).length !== this.state.options.length;

    if (hasNewOptions || hasChangedOptions) {
      this.setState({
        options: nextOptions,
      });
    }
    if (
      (nextProps.suggestedIds || []).length !== (this.props.suggestedIds || []).length ||
      _.difference(nextProps.suggestedIds, this.props.suggestedIds).length > 0
    ) {
      this.setState({
        focusedOptionId: undefined,
      });
    }

    if (nextProps.lastOpenedAt && nextProps.lastOpenedAt !== this.props.lastOpenedAt) {
      this.handleOpen();
      this.scheduleFocus();
    }

    if (nextProps.openRequestedAt && nextProps.openRequestedAt !== this.props.openRequestedAt) {
      this.handleOpen();
      this.scheduleFocus();
    }
  }

  scheduleFocus() {
    // Defer to next task so the click that triggered the open finishes its
    // blur cascade first. Refocuses even when the menu was already open
    // (e.g. clicking "Apply Topics" while the input had focus).
    clearTimeout(this._scheduledFocus);
    this._scheduledFocus = setTimeout(() => this.opener && this.opener.focus(), 0);
  }

  /* api */

  blur() {
    this.opener.blur();
  }

  focus() {
    this.handleOpen();
    this.opener.focus();
  }

  /* event handlers */

  handleBlur() {
    // determine if a custom attribute option has been focused. if so, do not close the menu
    const focusedOptionId = this.state.focusedOptionId || this.getInitialFocusedOptionId();
    const option = _.find(this.props.options, t => t.id === focusedOptionId);
    if (option && option.type === TopicsMenuOptionType.CUSTOM_ATTRIBUTE) {
      return;
    }

    if (this.isCancelled) {
      return;
    }
    this.saveOptionChanges();
    this.canClose = true;
    this.blur();
    this.handleClose();
  }

  handleApply(ev) {
    this.overrideEvent(ev);
    this.saveOptionChanges();
    this.closeMenu();
  }

  handleCancel(ev) {
    this.isCancelled = true;
    this.overrideEvent(ev);
    this.closeMenu();
  }

  closeMenu() {
    this.opener.focus();
    this.canClose = true;
    this.blur();
  }

  handleClose() {
    if (this.ignoreClose) {
      this.opener.focus();
      this.ignoreClose = false;
      return;
    }
    if (this.canClose) {
      this.props.onClose && this.props.onClose();
      this.props.onMenuOpenChange && this.props.onMenuOpenChange(false);
      this.canClose = false;
      this.setState({
        opened: false,
        filter: '',
        selectionsExpanded: false,
      });
    }
  }

  handleCollapseSelections(ev) {
    this.overrideEvent(ev);
    this.collapseSelections();
  }

  handleEnter() {
    let option = _.find(this.filteredOptions, o => o.optionId === this.focusedOptionId);
    switch (option && option.type) {
      case TopicsMenuOptionType.OPTION:
        if (option.optionType === TopicsMenuOptionType.CUSTOM_ATTRIBUTE && !option.selected) {
          const inputRef = this.menu.getMenuOptionInputRef(option.id);
          if (inputRef?.current && inputRef.current.value === '') {
            // Focus the input field for the custom attribute option
            inputRef.current.focus();
          } else {
            this.opener.focus();
          }
        } else {
          this.toggleOptionSelection(option);
          this.opener.focus();
          this.setState({ filter: '', focusedOptionId: this.getNextFilteredOptionId() });
        }
        break;

      case TopicsMenuOptionType.EXPANDER:
        this.expandSelections();
        this.setState({ focusedOptionId: TopicsMenuOptionType.COLLAPSER });
        break;

      case TopicsMenuOptionType.COLLAPSER:
        this.collapseSelections();
        this.setState({ focusedOptionId: TopicsMenuOptionType.EXPANDER });
        break;

      default:
        return;
    }
  }

  handleExpandSelections(ev) {
    this.overrideEvent(ev);
    this.expandSelections();
  }

  handleHeaderClick() {
    this.resetFocusedOptionId();
  }

  handleInputChange(ev) {
    let input = ev.target.value;
    this.setState({ filter: input });
    this.filteredOptions = this.buildFilteredOptions(input);
    this.resetFocusedOptionId();
    this.props.onSearch && this.props.onSearch();
  }

  handleInputKeyDown(ev) {
    if (!this.state.opened) return;

    this.suppressNextMouseEvent = true;
    const hasOptions = this.filteredOptions.length > 0;

    switch (ev.keyCode) {
      case 9: // tab
        this.overrideEvent(ev);
        this.saveOptionChanges();
        this.closeMenu();
        break;
      case 13: // enter
        this.overrideEvent(ev);
        this.handleEnter();
        break;
      case 27: {
        // esc
        this.overrideEvent(ev);

        const option = _.find(this.props.options, t => t.id === this.state.focusedOptionId);
        if (option && option.type !== TopicsMenuOptionType.CUSTOM_ATTRIBUTE) {
          this.closeMenu();
          return;
        }

        this.saveOptionChanges();
        this.canClose = true;
        this.blur();
        this.handleClose();
        break;
      }
      case 37: // left
        if ((ev.metaKey || ev.ctrlKey) && this.state.selectionsExpanded) {
          this.overrideEvent(ev);
          this.collapseSelections();
          hasOptions && this.setFocusedOptionId(TopicsMenuOptionType.EXPANDER);
        }
        break;
      case 38: // up
        this.overrideEvent(ev);
        if (ev.metaKey || ev.ctrlKey) {
          this.menu.scrollToTop();
          this.resetFocusedOptionId();
        } else {
          hasOptions && this.setFocusedOptionId(this.getPreviousFilteredOptionId());
        }
        break;
      case 39: // right
        if ((ev.metaKey || ev.ctrlKey) && !this.state.selectionsExpanded) {
          this.overrideEvent(ev);
          this.expandSelections();
          hasOptions && this.setFocusedOptionId(TopicsMenuOptionType.COLLAPSER);
        }
        break;
      case 40: // down
        this.overrideEvent(ev);
        if (ev.metaKey || ev.ctrlKey) {
          this.menu.scrollToBottom();
          this.setFocusedOptionId(this.getLastFilteredOptionId());
        } else {
          hasOptions && this.setFocusedOptionId(this.getNextFilteredOptionId());
        }
        break;
      default:
        break;
    }
  }

  handleMenuFocus(ev) {
    this.ignoreClose = true;
  }

  handleMenuOpenerClick(event) {
    if (this._closedByToggle) {
      this._closedByToggle = false;
      return;
    }
    if (!this.state.opened) {
      this.focus();
      this.props.onFocus && this.props.onFocus(this);
    }
  }

  handleMenuOpenerToggleDown(event) {
    // On mobile, touchstart fires before blur. On desktop, mousedown fires before blur.
    // Either way, we capture the open state before blur closes the menu.
    if (this.state.opened) {
      this._closedByToggle = true;
    }
  }

  handleOpen() {
    this.props.onOpen && this.props.onOpen();
    this.props.onMenuOpenChange && this.props.onMenuOpenChange(true);
    this.resetFocusedOptionId();
    this.setState({
      opened: true,
    });
    this.suppressNextMouseEvent = true;
    this.isCancelled = false;
  }

  /* process clicking option on mouse down to precede react-select's internal processing */
  handleOptionClick(ev, option) {
    this.overrideEvent(ev);
    this.toggleOptionSelection(option);
    this.setFocusedOptionId(this.getNextFilteredOptionId());
    this.setState({ filter: '' });
  }

  handleOptionFocus(option) {
    /* mouse and keyboard interaction are a bit funky. When we scroll the menu to focus an option currently out
     * of view, the mouse enters a new option which we would focus, removing focus on the one navigated to by
     * keyboard. This prevents the behavior.
     */
    if (this.suppressNextMouseEvent) {
      this.suppressNextMouseEvent = false;
      return;
    }
    this.setFocusedOptionId(option.optionId);
  }

  handleOutsideClick() {
    if (this.state.opened) {
      this.props.onOptionChange(this.state.selectedValues);
      this.props.onClose && this.props.onClose();
      this.props.onMenuOpenChange && this.props.onMenuOpenChange(false);
      this.canClose = false;
      this.setState({
        opened: false,
        filter: '',
        selectionsExpanded: false,
      });
    }
  }

  handleSubmit(attributeValue) {
    if (_.some(this.state.selectedValues.attributeValues, attributeValue)) {
      this.setState({
        filter: '',
      });
      return;
    }

    const selectedValues = {
      ...this.state.selectedValues,
      attributeValues: this.state.selectedValues.attributeValues.concat(attributeValue),
    };

    const nextOptions = this.getOptions(
      this.state.options,
      this.props.options,
      selectedValues,
      this.props.suggestedIds || []
    );
    this.setState({
      selectedValues,
      options: nextOptions,
      filter: '',
    });
  }

  /* presentation */

  render() {
    let className = classnames('conversationTopic-multi', this.props.className);

    let options = this.buildFilteredOptions(this.state.filter);
    this.filteredOptions = options;
    if (options.length > 0) {
      this.focusedOptionId = this.state.focusedOptionId || this.getInitialFocusedOptionId();
    }
    return (
      <div
        className={className}
        data-aid="conversationTopic-multi"
        onBlur={this.handleBlur}
        onClick={this.handleMenuOpenerClick}
        onKeyDown={this.handleInputKeyDown}
        onMouseDown={this.handleMenuOpenerToggleDown}
        onTouchStart={this.handleMenuOpenerToggleDown}
      >
        {this.renderMenuOpener()}
        {this.renderMenu(options)}
      </div>
    );
  }

  renderMenu(options) {
    if (!this.state.opened) {
      return null;
    }
    return (
      <OutsideClickHandler onClickOutside={this.handleOutsideClick}>
        <ConversationTopicsMenuV2
          dropDown={this.props.dropDown}
          focusedOptionId={this.focusedOptionId}
          hideSelectedOptions={this.props.hideSelectedOptions}
          numSelectedOptions={
            this.state.selectedValues.optionIds.length + this.state.selectedValues.attributeValues.length
          }
          onApply={this.handleApply}
          onCancel={this.handleCancel}
          onCollapserClick={this.handleCollapseSelections}
          onExpanderClick={this.handleExpandSelections}
          onFocus={this.handleMenuFocus}
          onHeaderClick={this.handleHeaderClick}
          onOptionClick={this.handleOptionClick}
          onOptionFocus={this.handleOptionFocus}
          onSubmit={this.handleSubmit}
          options={options}
          ref={this.setMenuRef}
          unitLabelPlural={this.props.unitLabelPlural}
          unitLabelSingular={this.props.unitLabelSingular}
        />
      </OutsideClickHandler>
    );
  }

  renderMenuOpener() {
    const selectedTopics = _.compact(
      _.map(this.state.selectedValues.optionIds, id => _.find(this.props.options, { id })?.name)
    );

    const selectedCustomAttributes = _.compact(
      _.map(this.state.selectedValues.attributeValues, ({ id, value }) => {
        let option = _.find(this.props.options, { id });
        return option && `${option.name} > ${value}${option.disabled ? ' (Archived)' : ''}`;
      })
    );

    const sortedTopicsByName = _.sortBy([...selectedTopics, ...selectedCustomAttributes], value => value.toLowerCase());

    return (
      <ConversationTopicsMenuOpenerV2
        inputValue={this.state.filter}
        isOpen={this.state.opened}
        onInputBlur={this.handleClose}
        onInputChange={this.handleInputChange}
        placeholder={this.props.placeholder}
        ref={this.setOpenerRef}
        values={sortedTopicsByName}
      />
    );
  }

  setOpenerRef(ref) {
    this.opener = ref;
  }

  /* utility */

  collapseSelections() {
    this.setState({
      selectionsExpanded: false,
    });
  }

  expandSelections() {
    this.setState({
      selectionsExpanded: true,
    });
  }

  buildFilteredOptions(filter) {
    return filterTopicOptions(
      this.state.options,
      filter,
      this.state.selectedValues.optionIds,
      this.state.selectedValues.attributeValues,
      {
        suggestedLabel: this.props.suggestedLabel,
        unselectedLabel: this.props.unselectedLabel,
        enhancedTopicWorkflows: this.props.enhancedTopicWorkflows,
        collapseSelections: selectedOptions => this.collapseOrExpandOptions(selectedOptions),
      }
    );
  }

  collapseOrExpandOptions(selectedOptions) {
    /* collapse options or add ability to collapse them */
    if (selectedOptions.length > COLLAPSE_SELECTED_OPTIONS_NUM) {
      if (this.state.selectionsExpanded) {
        selectedOptions.push({
          disabled: true,
          type: TopicsMenuOptionType.COLLAPSER,
          optionId: TopicsMenuOptionType.COLLAPSER,
        });
      } else {
        let numCollapsedOptions = selectedOptions.length - COLLAPSE_SELECTED_OPTIONS_NUM;
        selectedOptions.splice(COLLAPSE_SELECTED_OPTIONS_NUM, numCollapsedOptions, {
          disabled: true,
          type: TopicsMenuOptionType.EXPANDER,
          optionId: TopicsMenuOptionType.EXPANDER,
        });
      }
    }
    return selectedOptions;
  }

  getInitialSelectedValues() {
    const optionIds = this.props.optionIds || [];
    const attributeValues = this.props.selectedCustomAttributes || [];
    return { attributeValues, optionIds };
  }

  getNextFilteredOptionId() {
    let option;
    let index = _.findIndex(this.filteredOptions, o => o.optionId === this.focusedOptionId);
    do {
      index = (index + 1) % this.filteredOptions.length;
      option = this.filteredOptions[index];
    } while (option.unfocusable);
    return option.optionId;
  }

  getPreviousFilteredOptionId() {
    let option;
    let index = _.findIndex(this.filteredOptions, o => o.optionId === this.focusedOptionId);
    do {
      index--;
      if (index < 0) {
        index = this.filteredOptions.length - 1;
      }
      option = this.filteredOptions[index];
    } while (option.unfocusable);
    return option.optionId;
  }

  getLastFilteredOptionId() {
    let last = _.last(this.filteredOptions);
    return last.optionId;
  }

  getOptions(currentOptions, newOptions, selectedValues, suggestedValues) {
    const selectedSet = new Set(selectedValues.optionIds);
    const suggestedSet = new Set(suggestedValues);
    const wasSelectedSet = new Set(_.filter(currentOptions || [], o => o.wasSelected));
    return newOptions.map(t => {
      let id = t.id;
      let suggested = suggestedSet.has(id);
      let selected = selectedSet.has(id);
      let wasSelected = wasSelectedSet.has(id) || selected;
      return {
        archived: t.disabled, // Can't use 'disabled' as key since that affects react-select in undesirable way.
        categoryOnly: t.categoryOnly,
        label: t.name,
        parentId: t.parentId,
        selected,
        wasSelected,
        type: TopicsMenuOptionType.OPTION,
        suggested,
        optionId: id,
        optionType: t.type,
        id,
      };
    });
  }

  overrideEvent(ev) {
    ev.stopPropagation();
    ev.preventDefault();
  }

  getInitialFocusedOptionId() {
    if (this.filteredOptions.length > 0) {
      let firstTitleIndex = _.findIndex(this.filteredOptions, { type: TopicsMenuOptionType.GROUP_TITLE });
      if (firstTitleIndex !== -1) {
        return this.filteredOptions[firstTitleIndex + 1].optionId;
      }
      let dividerIndex = _.findIndex(this.filteredOptions, { type: TopicsMenuOptionType.DIVIDER });
      if (dividerIndex !== -1) {
        return this.filteredOptions[dividerIndex + 1].optionId;
      }
      return this.filteredOptions[0] && this.filteredOptions[0].optionId;
    }
  }

  resetFocusedOptionId() {
    this.setFocusedOptionId(this.getInitialFocusedOptionId());
  }

  saveOptionChanges() {
    this.props.onOptionChange(this.state.selectedValues);
  }

  setFocusedOptionId(optionId) {
    this.opener.focus();
    this.focusedOptionId = optionId;
    this.setState({
      focusedOptionId: optionId,
    });
  }

  setMenuRef(ref) {
    this.menu = ref;
  }

  toggleOptionSelection(option) {
    if (option.disabled || (option.optionType === TopicsMenuOptionType.CUSTOM_ATTRIBUTE && !option.selected)) {
      // no-op if unselected custom attribute is clicked
      return;
    }

    const selectionType = option.suggested ? 'suggestion' : 'general-list';
    const action = option.selected ? SelectAction.REMOVE : SelectAction.ADD;

    this.props.onSelect &&
      this.props.onSelect({
        topicId: option.id,
        topicName: option.label,
        action,
        type: option.optionType,
        selectionType,
      });

    const optionIds = option.selected
      ? _.filter(this.state.selectedValues.optionIds, v => v !== option.id)
      : [...this.state.selectedValues.optionIds, option.id];

    let selectedValues;
    if (option.optionType === TopicsMenuOptionType.TOPIC) {
      selectedValues = { ...this.state.selectedValues, optionIds };
    } else {
      selectedValues = {
        ...this.state.selectedValues,
        attributeValues: _.filter(
          this.state.selectedValues.attributeValues,
          attr => !(attr.id === option.id && attr.value === option.optionValue)
        ),
      };
    }

    let nextOptions = this.getOptions(
      this.state.options,
      this.props.options,
      selectedValues,
      this.props.suggestedIds || []
    );
    this.setState({ selectedValues, options: nextOptions });
  }
}

ConversationTopicsMultiSelectV2.propTypes = {
  className: PropTypes.string,
  dropDown: PropTypes.bool,
  enhancedTopicWorkflows: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  label: PropTypes.string,
  lastOpenedAt: PropTypes.string,
  optionIds: PropTypes.array.isRequired,
  onClose: PropTypes.func,
  onFocus: PropTypes.func,
  onMenuOpenChange: PropTypes.func,
  onOpen: PropTypes.func,
  openRequestedAt: PropTypes.string,
  onSearch: PropTypes.func,
  onSelect: PropTypes.func,
  onOptionChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  selectedCustomAttributes: PropTypes.arrayOf(PropTypes.instanceOf(CustomAttributeValue)),
  suggestedIds: PropTypes.array,
  suggestedLabel: PropTypes.string,
  unitLabelSingular: PropTypes.string.isRequired,
  unitLabelPlural: PropTypes.string,
  unselectedLabel: PropTypes.string,
};
